import { useCallback, useEffect, useRef, useState } from "react";
import api from "../services/api";
import { getSocket } from "../services/socket-io";
import {
  NO_STATE_KEY,
  appendCards,
  buildBoardParams,
  colKey,
  findCard,
  hasAnyFilter,
  matchesDateFilter,
  matchesQueueFilter,
  matchesUserFilter,
  mergeTotals,
  moveCard,
  normalizeColumns,
  patchCard,
  removeCard,
  targetKeyForState,
} from "../components/CRMBoard/boardState";

// feat_395 — Tablero CRM (frontend del feat_394).
//
// Endpoints (todos isAuth + getTenantId; el companyId sale del JWT, el front
// NUNCA lo manda):
//   GET /crm/status             → { enabled, hasActiveStates }
//   GET /crm/board              → { columns: [...] }
//   PUT /contacts/:id/state     → cambio de estado (source:"board")
// ⚠ NO usar /api/contacts/... : es el endpoint de token externo (n8n).

export const PAGE_LIMIT = 30;

// Estado del módulo. `settingValue` es el crmBoardEnabled del SettingsContext:
// cuando el admin prende/apaga el switch llega por socket "settings" y esto
// vuelve a pedir /crm/status (hasActiveStates no tiene socket propio).
export const useCrmStatus = settingValue => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/crm/status");
      setStatus({ enabled: !!data?.enabled, hasActiveStates: !!data?.hasActiveStates });
    } catch (_) {
      setStatus({ enabled: false, hasActiveStates: false });
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    reload();
  }, [reload, settingValue]);

  return { status, loading, reload, available: !!(status?.enabled && status?.hasActiveStates) };
};

// `filters` llega YA memoizado por el caller y `filterKey` es su JSON: las
// deps de los effects usan la clave (string) para no refetchear en loop por una
// referencia nueva en cada render (regla filterKey de feat_301/326).
const useCrmBoard = ({ enabled, filters, filterKey, isAdmin }) => {
  const [columns, setColumnsState] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState({});
  const [error, setError] = useState(null);
  // feat_397 — etapa de entrada (undefined = todavía no cargó; null = la
  // empresa no eligió ninguna). La usa el socket y el aviso del tablero.
  const [entryStateId, setEntryStateId] = useState(undefined);
  const entryKeyRef = useRef(null);
  const columnsRef = useRef([]);
  const filtersRef = useRef(filters);
  filtersRef.current = filters;
  const requestSeq = useRef(0);
  const totalsTimer = useRef(null);

  const setColumns = useCallback(updater => {
    setColumnsState(prev => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      columnsRef.current = next;
      return next;
    });
  }, []);

  const load = useCallback(async () => {
    if (!enabled) return;
    const seq = ++requestSeq.current;
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get("/crm/board", {
        params: buildBoardParams(filtersRef.current, { limit: PAGE_LIMIT }),
      });
      if (seq !== requestSeq.current) return;
      const entry = data?.entryStateId ?? null;
      entryKeyRef.current = entry === null ? null : colKey(entry);
      setEntryStateId(entry);
      setColumns(normalizeColumns(data?.columns));
    } catch (err) {
      if (seq !== requestSeq.current) return;
      setError(err);
    }
    if (seq === requestSeq.current) setLoading(false);
  }, [enabled, setColumns]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load, filterKey]);

  const loadMore = useCallback(async key => {
    const col = columnsRef.current.find(c => c.key === key);
    if (!col) return;
    setLoadingMore(prev => ({ ...prev, [key]: true }));
    try {
      const { data } = await api.get("/crm/board", {
        params: buildBoardParams(filtersRef.current, {
          limit: PAGE_LIMIT,
          stateId: key === NO_STATE_KEY ? "none" : key,
          offset: col.cards.length,
        }),
      });
      const page = (data?.columns || []).find(c => colKey(c.stateId) === key);
      if (page) setColumns(prev => appendCards(prev, key, page));
    } catch (err) {
      setError(err);
    }
    setLoadingMore(prev => ({ ...prev, [key]: false }));
  }, [setColumns]);

  // Totales reales (limit=1) con debounce: se usa cuando llega un evento de un
  // contacto que NO está cargado — no hay forma de saber en el cliente si
  // cumple los filtros, el backend sí.
  const scheduleTotalsRefresh = useCallback(() => {
    if (totalsTimer.current) clearTimeout(totalsTimer.current);
    totalsTimer.current = setTimeout(async () => {
      totalsTimer.current = null;
      try {
        const { data } = await api.get("/crm/board", {
          params: buildBoardParams(filtersRef.current, { limit: 1 }),
        });
        setColumns(prev => mergeTotals(prev, data?.columns));
      } catch (_) {
        /* silencioso: el próximo evento o un refresh manual lo corrige */
      }
    }, 1500);
  }, [setColumns]);

  useEffect(() => () => totalsTimer.current && clearTimeout(totalsTimer.current), []);

  const changeState = useCallback(
    (contactId, { stateId, lostReasonId, lostReasonText }) => {
      const body = { stateId: stateId ?? null, source: "board", autoClose: true };
      if (lostReasonId) body.lostReasonId = lostReasonId;
      const text = typeof lostReasonText === "string" ? lostReasonText.trim() : "";
      if (text) body.lostReasonText = text;
      return api.put(`/contacts/${contactId}/state`, body);
    },
    []
  );

  // ── Sockets ────────────────────────────────────────────────────────────────
  // getSocket() es el singleton COMPARTIDO: cleanup sólo con off(handler),
  // NUNCA disconnect(). Los filtros viven en filtersRef (siempre frescos) y
  // filterKey está en las deps para re-suscribir si cambian (feat_325/326/327).
  useEffect(() => {
    if (!enabled) return undefined;
    const socket = getSocket();

    const handleContactState = data => {
      if (!data || !data.contactId) return;
      const contactId = Number(data.contactId);
      const toKey = targetKeyForState(columnsRef.current, contactId, data.stateId ?? null, entryKeyRef.current);
      const next = moveCard(columnsRef.current, contactId, toKey);
      if (next === null) {
        // No está cargada: el total de origen/destino cambió, pero sólo el
        // backend sabe si cumple los filtros.
        scheduleTotalsRefresh();
        return;
      }
      if (next !== columnsRef.current) setColumns(next);
      if (!columnsRef.current.some(c => c.key === toKey)) scheduleTotalsRefresh();
    };

    const applyTicketToCard = (ticket, freshAt, senderType) => {
      const contactId = ticket?.contact?.id || ticket?.contactId;
      if (!contactId) return;
      const found = findCard(columnsRef.current, Number(contactId));
      if (!found) return;
      const cur = found.card.ticket || {};
      // El ticket de la ficha es el "elegido" por el backend (y, para un
      // no-admin, dentro de SU scope). Sólo se pisa con el mismo ticket, o —para
      // un admin— con un ticket vivo nuevo del contacto. Así un no-admin nunca
      // ve el último mensaje de una conversación ajena.
      const sameTicket = cur.id && cur.id === ticket.id;
      const canAdopt = isAdmin && ticket.status !== "closed";
      if (!sameTicket && !canAdopt) return;
      // feat_396 — un ticket nuevo de una cola fuera del filtro no se adopta.
      if (!sameTicket && !matchesQueueFilter(ticket, filtersRef.current)) return;

      const nextTicket = {
        ...cur,
        id: ticket.id,
        status: ticket.status ?? cur.status,
        userId: ticket.userId ?? null,
        userName: ticket.user?.name ?? (ticket.userId === cur.userId ? cur.userName : null),
        queueId: ticket.queueId ?? cur.queueId,
        lastMessage: ticket.lastMessage ?? cur.lastMessage,
        lastMessageSenderType: senderType || ticket.lastMessageSenderType || cur.lastMessageSenderType,
      };
      const lastActivityAt = freshAt || found.card.lastActivityAt;
      const f = filtersRef.current;
      if (
        !matchesUserFilter(nextTicket, f) ||
        !matchesDateFilter(lastActivityAt, f) ||
        !matchesQueueFilter(nextTicket, f)
      ) {
        setColumns(prev => removeCard(prev, Number(contactId)));
        scheduleTotalsRefresh();
        return;
      }
      setColumns(prev => patchCard(prev, Number(contactId), c => ({ ...c, ticket: nextTicket, lastActivityAt })));
    };

    const handleTicket = data => {
      if (data?.action !== "update" && data?.action !== "updateAndBump") return;
      if (!data.ticket) return;
      applyTicketToCard(data.ticket, null, null);
    };

    // Sólo mensajes reales cuentan como actividad (mismo criterio que el
    // backend para lastActivityAt: notas internas y marcadores no).
    const IGNORED_SENDERS = ["internal-note", "scheduled-event", "matchprop-alert"];
    const handleAppMessage = data => {
      if (data?.action !== "create" || !data.ticket || !data.message) return;
      const senderType = data.message.senderType;
      if (IGNORED_SENDERS.includes(senderType)) return;
      applyTicketToCard(data.ticket, data.message.createdAt || new Date().toISOString(), senderType);
    };

    const handleContact = data => {
      if (!data?.contact?.id) return;
      const id = Number(data.contact.id);
      if (data.action === "delete") {
        setColumns(prev => removeCard(prev, id));
        return;
      }
      if (data.action === "update") {
        setColumns(prev => patchCard(prev, id, c => ({ ...c, contact: { ...c.contact, ...data.contact } })));
      }
    };

    socket.on("contactState", handleContactState);
    socket.on("ticket", handleTicket);
    socket.on("appMessage", handleAppMessage);
    socket.on("contact", handleContact);
    return () => {
      socket.off("contactState", handleContactState);
      socket.off("ticket", handleTicket);
      socket.off("appMessage", handleAppMessage);
      socket.off("contact", handleContact);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, filterKey, isAdmin, scheduleTotalsRefresh, setColumns]);

  return {
    columns,
    columnsRef,
    setColumns,
    loading,
    loadingMore,
    error,
    reload: load,
    loadMore,
    changeState,
    hasFilters: hasAnyFilter(filters),
    entryStateId,
  };
};

export default useCrmBoard;
